import { Col, Form, Loading, Row, SelectField } from '@/components';
import { BudgetTypeSearchFormSchema } from '@/features/master-table/models';
import { useFiscalYearOptions } from '@/features/search/_hooks/budget-types';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import type { z } from 'zod';

type FormValues = z.infer<typeof BudgetTypeSearchFormSchema>;

export const BudgetTypeSearchForm: FC = () => {
  const { t } = useTranslation();
  const currentFiscalYear = useCurrentFiscalYear();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentParams = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams],
  );
  const {
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      fiscalYear:
        currentParams.fiscalYear ??
        currentFiscalYear.data?.toString() ??
        currentFiscalYear.basicFiscalYear.toString(),
    },
    resolver: zodResolver(BudgetTypeSearchFormSchema),
  });

  const fiscalYearOptions = useFiscalYearOptions();

  if (fiscalYearOptions.isPending || !fiscalYearOptions.data) {
    return <Loading />;
  }

  return (
    <Form
      leavePageAlertCondition={null}
      layout="vertical"
      style={{
        marginBottom: currentParams.fiscalYear === 'all' ? 12 : undefined,
      }}
    >
      <Row gutter={16}>
        <Col span={3}>
          <SelectField
            name="fiscalYear"
            label={t('fiscal-year')}
            control={control}
            errors={errors}
            options={[
              { label: t('all-fiscal-year'), value: 'all' },
              ...fiscalYearOptions.data,
            ]}
            allowClear={false}
            shouldCoexistOnChange
            onChange={(value) => {
              setSearchParams({ fiscalYear: value }, { replace: true });
            }}
            wrapperStyle={{ marginBottom: 0 }}
          />
        </Col>
      </Row>
    </Form>
  );
};
